import React, {useContext, useEffect, useState} from "react";
import {
    AppBar,
    Badge,
    Box,
    Button,
    Chip, IconButton,
    ListItemText,
    Menu,
    MenuItem,
    Toolbar,
    Tooltip,
    Typography,
} from "@mui/material";
import {useNavigate} from "react-router-dom";
import HelpIcon from "@mui/icons-material/Help";
import {SnackbarContext} from "../../shared/store";
import {ReleaseNotes} from "../ReleaseNote/ReleaseNote";
import {
    accountButtonSx, accountMenuSx,
    appBarSx,
    helperLinkBox,
    helpIcon, logoAndTitleContainerSx, neRelicToolboxLogoSx,
    newRelicToolboxTitleSx,
    profileBox,
    toolbarSx, welcomeTextSx,
} from "./styles";
import {alertStyle} from "../../styles";
import SnackbarComponent from "../../components/SnackBar/SnackBar";
import {useAuth} from "../../auth/AuthContext";
import theme from "../../theme";

interface Snackbar {
    id: number;
    open: boolean;
    boldAlertMsg: string;
    normalAlertMsg: string;
    severity: string;
}


/** Header bar with logo, title, helper menu, snackbars, and release notes modal. */
function Header() {
    const {isAuthenticated, account, login, logout} = useAuth();
    const navigate = useNavigate();

    const accountGivenName = account?.givenName ?? '';

    const navigator = useNavigate();

    // Menu anchors
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [helperAnchorEl, setHelperAnchorEL] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const helperOpen = Boolean(helperAnchorEl);


    const [releaseNotesOpen, setReleaseNotesOpen] = useState(false);
    const [isNewRelease, setIsNewRelease] = useState(false);

    // Snackbar state
    const {snackbars, infoSnackbar, setInfoSnackbar} = useContext(SnackbarContext);
    const [infoSnackbarCurr, setInfoSnackbarCurr] = useState<Snackbar | null>(null);
    const [snackbarsCurr, setSnackbarsCurr] = useState<Snackbar[]>([]);

    useEffect(() => {
        setSnackbarsCurr(snackbars);
        setInfoSnackbarCurr(infoSnackbar);
    }, [snackbars, infoSnackbar]);

    /** Refresh NEW badge based on localStorage markers. */
    const showLatestRelease = () => {
        const latest = localStorage.getItem("releaseVersion");
        const viewed = localStorage.getItem("viewedReleaseVersion");
        setIsNewRelease(latest !== viewed);
    };

    useEffect(() => {
        showLatestRelease();
    }, []);

    const openReleaseNotes = () => {
        setReleaseNotesOpen(true);
        showLatestRelease();
    };
    const closeReleaseNotes = () => {
        setReleaseNotesOpen(false);
        showLatestRelease();
    };

    const handleClick: any = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleHelperClick = (e: React.MouseEvent<HTMLElement>) => setHelperAnchorEL(e.currentTarget);

    const handleClose = () => {
        setAnchorEl(null);
    }
    const handleHelperClose = () => {
        setHelperAnchorEL(null);
    }

    const handleSignIn = async () => {
        await login(); // if mock auth: flips on; if msal auth: popup/redirect
        navigate("/", {replace: true}); // reset their url; this should send them to the dashboard
    }
    const handleSignOut = async () => {
        await logout(); // if mock auth: clears;  if msal auth: logoutRedirect()
        navigate("/") // reset their url; this should send them to the public welcome page.
    }

    return (<Box>
        <AppBar sx={appBarSx}>
            <Toolbar sx={toolbarSx}>
                {/* Left: Logo and Title */}
                <Box sx={logoAndTitleContainerSx} onClick={() => navigator("/")}>
                    <Box component="img" src={'/NRTBLogo.png'} alt="New Relic Toolbox" sx={neRelicToolboxLogoSx}/>

                    <Typography sx={newRelicToolboxTitleSx}>
                        NEW RELIC
                        <br/>
                        TOOLBOX
                    </Typography>
                </Box>

                {/* Right: Help, Auth, & Release Notes*/}
                <Box sx={{ display: 'flex' }}>
                    <Box sx={profileBox}>
                        <Box sx={helperLinkBox} onClick={handleHelperClick}>
                            <Tooltip title="Help">
                                {isNewRelease ? (<Badge
                                    color="info"
                                    badgeContent="NEW"
                                    variant="standard"
                                    anchorOrigin={{vertical: "top", horizontal: "left"}}
                                >
                                    <HelpIcon data-testid="helper-icon" sx={helpIcon}/>
                                </Badge>) : (<HelpIcon data-testid="helper-icon" sx={helpIcon}/>)}
                            </Tooltip>

                        </Box>
                    </Box>

                    {/* Helper menu */}
                    <Menu
                      anchorEl={helperAnchorEl}
                      id="helper-menu"
                      open={helperOpen}
                      onClose={handleHelperClose}
                      onClick={handleHelperClose}
                      autoFocus={false}
                    >
                      <MenuItem onClick={openReleaseNotes}>
                        <ListItemText>What’s new</ListItemText>
                        {isNewRelease && <Chip label="NEW" variant="outlined" size="small" color="primary" />}
                      </MenuItem>

                      {!isAuthenticated && (
                        <MenuItem onClick={() => navigator("/termsandconditions")}>
                          <ListItemText>Terms And Conditions</ListItemText>
                        </MenuItem>
                      )}
                    </Menu>


                    {/* Auth UI */}
                    {!isAuthenticated ? (<Button variant="contained" size="small" onClick={handleSignIn}>
                        Sign in
                    </Button>) : (<Button
                        id="account-button"
                        aria-controls={open ? 'account-menu' : undefined}
                        aria-haspopup="true"
                        aria-expanded={open ? 'true' : undefined}
                        onClick={handleClick}
                        variant="text"
                        size="small"
                        sx={accountButtonSx}
                    >
                        <Box sx={welcomeTextSx}>Welcome,&nbsp;</Box> {accountGivenName || 'Account'}
                    </Button>)}

                    {/* Account menu */}
                    <Menu
                        anchorEl={anchorEl}
                        id="account-menu"
                        open={open}
                        onClose={handleClose}
                        onClick={handleClose}
                        autoFocus={false}
                        transformOrigin={{horizontal: 'right', vertical: 'top'}}
                        anchorOrigin={{horizontal: 'right', vertical: 'bottom'}}
                        sx={accountMenuSx(theme)}>

                        <MenuItem onClick={openReleaseNotes}>
                            <ListItemText>Release Notes</ListItemText>
                            {isNewRelease && <Chip label="NEW" variant="outlined" size="small" color="secondary"/>}
                        </MenuItem>

                        {isAuthenticated && (<div>
                        <MenuItem onClick={() => navigator("/managesubscriptions")}>
                            <ListItemText>Manage Subscriptions</ListItemText>
                        </MenuItem>
                        <MenuItem onClick={() => navigator("/newRelicIntegration")}>
                            <ListItemText>Connect New Relic</ListItemText>
                        </MenuItem>
                        <MenuItem onClick={() => navigator("/settings")}>
                            <ListItemText>User Settings</ListItemText>
                        </MenuItem>
                        <MenuItem onClick={() => navigator("/termsandconditions")}>
                            <ListItemText>Terms And Conditions</ListItemText>
                        </MenuItem>
                        <MenuItem onClick={handleSignOut}>
                            <ListItemText>Sign out</ListItemText>
                        </MenuItem>
                        </div>)}
                    </Menu>


                </Box>
            </Toolbar>
        </AppBar>

        {/* Snackbars + Release Notes modal */}
        <Box sx={alertStyle}>
            {infoSnackbarCurr && (<SnackbarComponent
                id={infoSnackbarCurr.id}
                open={infoSnackbarCurr.open}
                boldAlertMsg={infoSnackbarCurr.boldAlertMsg}
                normalAlertMsg={infoSnackbarCurr.normalAlertMsg}
                severity={infoSnackbarCurr.severity}
                setInfoSnackbar={setInfoSnackbar}
            />)}
            {snackbarsCurr?.map((snackbar) => (<SnackbarComponent
                key={snackbar.id}
                id={snackbar.id}
                open={snackbar.open}
                boldAlertMsg={snackbar.boldAlertMsg}
                normalAlertMsg={snackbar.normalAlertMsg}
                severity={snackbar.severity}
                setInfoSnackbar={setInfoSnackbar}
            />))}
        </Box>

        <ReleaseNotes open={releaseNotesOpen} handleClose={closeReleaseNotes}/>
    </Box>);
}

export default Header;
